/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from "react";

interface DropdownProps {
  options: any[];
  label?: string;
  onSelect?: (value: string) => void;
  required?: boolean;
  value?: any;
  name?: string;
  placeholder?: string;
  error?: string;
  onSearch?: (query: string) => void;
}

const Dropdown: React.FC<DropdownProps> = ({
  label,
  options,
  onSelect,
  required,
  value,
  name,
  placeholder,
  error,
  onSearch,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Controlled selected option
  const selectedOption = options?.find((x) => x?.value === value) ?? null;

  const handleSelect = (option: any) => {
    onSelect && onSelect(option?.value ?? option);
    setIsOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Debounce search input
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (onSearch) {
        onSearch(searchTerm);
      } else {
        if (searchTerm) {
          setFilteredOptions(
            options?.filter((option: any) =>
              (option?.label ?? option)
                .toString()
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
            )
          );
        } else {
          setFilteredOptions(options);
        }
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchTerm, options]);

  // refresh filteredOptions whenever options change
  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  return (
    <div ref={dropdownRef} className="relative w-full mb-2">
      <label className="block text-sm font-medium mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {/* Dropdown button */}
      <div
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        tabIndex={0}
        className={`border rounded-md p-2 flex justify-between items-center cursor-pointer
          ${
            error
              ? "border-red-500 focus:ring-red-500"
              : isOpen
              ? "border-primary"
              : "border-gray-300"
          }
          focus:outline-none focus:ring-1 focus:ring-primary`}
      >
        <span>
          {selectedOption?.label ?? placeholder ?? "Select an option"}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-gray-500"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          role="menu"
          aria-labelledby={label}
          className="absolute mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 w-full max-h-[300px] overflow-y-auto"
        >
          <div className="p-2">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
          </div>
          {filteredOptions?.map((option: any, index: number) => (
            <div
              key={index}
              role="option"
              aria-selected={selectedOption === option}
              onClick={() => handleSelect(option)}
              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                selectedOption?.label === option?.label
                  ? "bg-gray-100 font-semibold"
                  : ""
              }`}
            >
              {option?.label ?? option}
            </div>
          ))}
          {!filteredOptions?.length && (
            <div className="px-4 py-2 text-gray-400">No results found</div>
          )}
        </div>
      )}
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default Dropdown;
